import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc, updateDoc, addDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '@/firebase';

interface Information {
    id: string;
    tag: string;
    title: string;
    contents: string;
    priority: number;
    createdAt: Timestamp;
}

export default function InformationManagement() {
    const navigate = useNavigate();
    const [informations, setInformations] = useState<Information[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingInfo, setEditingInfo] = useState<Information | null>(null);
    const [isAdding, setIsAdding] = useState(false);

    // Form state
    const [form, setForm] = useState<Partial<Information>>({
        tag: '重要',
        title: '',
        contents: '',
        priority: 1
    });

    useEffect(() => {
        fetchInformations();
    }, []);

    const fetchInformations = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, 'infomation'));
            const infoData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Information[];

            // Sort by priority
            infoData.sort((a, b) => {
                const pA = a.priority || 999;
                const pB = b.priority || 999;
                return pA - pB;
            });

            setInformations(infoData);
        } catch (error) {
            console.error('Error fetching informations:', error);
            alert('お知らせの取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (info: Information) => {
        setEditingInfo(info);
        setForm({ ...info });
    };

    const handleDelete = async (id: string) => {
        if (!confirm('このお知らせを削除してもよろしいですか？')) return;

        try {
            await deleteDoc(doc(db, 'infomation', id));
            alert('お知らせを削除しました');
            fetchInformations();
        } catch (error: any) {
            console.error('Error deleting information:', error);
            alert(`削除に失敗しました: ${error.message}`);
        }
    };

    const handleSave = async () => {
        if (!form.tag || !form.title || !form.contents) {
            alert('必須項目を入力してください');
            return;
        }

        try {
            const saveData = {
                tag: form.tag,
                title: form.title,
                contents: form.contents,
                priority: Number(form.priority) || 1,
                createdAt: editingInfo ? editingInfo.createdAt : Timestamp.now()
            };

            if (editingInfo) {
                await updateDoc(doc(db, 'infomation', editingInfo.id), saveData);
                alert('お知らせを更新しました');
            } else {
                await addDoc(collection(db, 'infomation'), saveData);
                alert('お知らせを追加しました');
            }

            setEditingInfo(null);
            setIsAdding(false);
            setForm({
                tag: '重要',
                title: '',
                contents: '',
                priority: 1
            });
            fetchInformations();
        } catch (error: any) {
            console.error('Error saving information:', error);
            alert(`保存に失敗しました: ${error.message}`);
        }
    };

    const openAddModal = () => {
        setIsAdding(true);
        setForm({
            tag: '重要',
            title: '',
            contents: '',
            priority: informations.length + 1
        });
    };

    const handleLogout = async () => {
        try {
            await auth.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const getTagColor = (tag: string) => {
        if (tag === '重要') return 'bg-[#6B96C3] text-white';
        if (tag === 'INFO') return 'bg-[#F4B183] text-white';
        return 'bg-gray-200 text-gray-700';
    };

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">お知らせ管理</h1>
                        <p className="text-gray-600">お知らせの追加・編集・削除を行います</p>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={openAddModal}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            ＋ お知らせ追加
                        </button>
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            ダッシュボードに戻る
                        </button>
                        <button
                            onClick={handleLogout}
                            className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            ログアウト
                        </button>
                    </div>
                </div>

                <div className="mb-4 text-sm text-gray-600 font-medium">
                    合計: {informations.length} 件
                </div>

                {/* Informations List */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center text-gray-500">読み込み中...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 w-24">優先度</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 w-32">タグ</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">タイトル</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">内容</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 w-40">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {informations.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                                お知らせが登録されていません
                                            </td>
                                        </tr>
                                    ) : (
                                        informations.map((info) => (
                                            <tr key={info.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 text-sm text-gray-800 text-center">{info.priority}</td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTagColor(info.tag)}`}>
                                                        {info.tag}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-800">
                                                    <div className="line-clamp-2">{info.title}</div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                    <div className="line-clamp-2">{info.contents}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleEdit(info)}
                                                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                                                        >
                                                            編集
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(info.id)}
                                                            className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                                                        >
                                                            削除
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit/Add Modal */}
            {(editingInfo || isAdding) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold text-gray-800">
                                {isAdding ? 'お知らせ追加' : 'お知らせ編集'}
                            </h2>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        タグ <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={form.tag}
                                        onChange={(e) => setForm({ ...form, tag: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="重要">重要</option>
                                        <option value="INFO">INFO</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        優先度 <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={form.priority}
                                        onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        min="1"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    タイトル <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="お知らせのタイトルを入力"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    内容 <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={form.contents}
                                    onChange={(e) => setForm({ ...form, contents: e.target.value })}
                                    rows={6}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="お知らせの内容を入力"
                                />
                            </div>
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setEditingInfo(null);
                                    setIsAdding(false);
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                保存
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
